import dayjs from 'dayjs';

import { OptionalTimeRange } from '@cast/shared-types';
import { SECONDS_IN_DAY, SECONDS_IN_HOUR } from '@cast/utils';

import { ResourceUtilizationChartType } from '../../types';

/** API max range for 5m (`stepSeconds === 300`) resource-usage queries. */
const FIVE_MINUTE_STEP_SECONDS = SECONDS_IN_HOUR / 12;
const FIVE_MINUTE_MAX_RANGE_SECONDS = SECONDS_IN_HOUR * 240;

export const getStep = (
  range: OptionalTimeRange<string>,
  type: ResourceUtilizationChartType
): number => {
  const isBar = type === ResourceUtilizationChartType.BAR;
  const diffInSeconds = dayjs(range.to).diff(dayjs(range.from), 'seconds');
  // 10-minute steps for bars, 5-minute steps for area charts
  if (diffInSeconds <= SECONDS_IN_HOUR) {
    return 300;
  }

  // 1-hour steps for bars, 5-minute steps for area charts
  if (diffInSeconds <= SECONDS_IN_DAY) {
    return isBar ? 3600 : 300;
  }

  // 1-day steps for bars, 5-minute steps for area charts
  if (diffInSeconds <= SECONDS_IN_DAY * 10) {
    return isBar ? SECONDS_IN_DAY : 300;
  }

  // 1-day steps for any type when selecting more than 31 days
  if (diffInSeconds > SECONDS_IN_DAY * 31) {
    return SECONDS_IN_DAY;
  }

  // 1-day steps for bars, 1-hour steps for area charts
  return isBar ? SECONDS_IN_DAY : SECONDS_IN_HOUR;
};

type GetResourceUsageApiTimeRangeArgs = {
  range: OptionalTimeRange<string>;
  timeZone: string;
  stepSeconds: number;
};

/**
 * API window for resource-usage. Pads start by one step (bucket-end timestamps).
 * Skips the 5m pad when it would exceed the API's 240h max for that granularity.
 */
export const getResourceUsageApiTimeRange = ({
  range,
  timeZone,
  stepSeconds,
}: GetResourceUsageApiTimeRangeArgs): {
  startTime: string;
  endTime: string;
} => {
  const isMultiDay =
    dayjs(range.to).diff(dayjs(range.from), 'seconds') > SECONDS_IN_DAY;
  const from = dayjs(range.from).tz(timeZone);
  const to = dayjs(range.to).tz(timeZone);
  const start = isMultiDay ? from.startOf('day') : from;
  const end = isMultiDay ? to.endOf('day') : to;

  const paddedStart =
    stepSeconds === SECONDS_IN_DAY
      ? start.subtract(1, 'day')
      : start.subtract(stepSeconds, 'seconds');

  const shouldSkipPad =
    stepSeconds === FIVE_MINUTE_STEP_SECONDS &&
    end.diff(paddedStart, 'seconds') > FIVE_MINUTE_MAX_RANGE_SECONDS;
  const shouldUseUnalignedRange =
    stepSeconds === FIVE_MINUTE_STEP_SECONDS &&
    end.diff(start, 'seconds') > FIVE_MINUTE_MAX_RANGE_SECONDS;

  return {
    startTime: (shouldUseUnalignedRange
      ? from
      : shouldSkipPad
      ? start
      : paddedStart
    ).toISOString(),
    endTime: (shouldUseUnalignedRange ? to : end).toISOString(),
  };
};
